import { useEffect, useState } from "react"
import { CheckCircle2, ShieldCheck } from "lucide-react"
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { AlgoBuilderLogo, Button, Separator } from "@/components"
import { api } from "../system/api.js"
import { getSymbolCurrency } from "../system/util.js"
import { getStripePromise } from "../system/stripePromise.js"
import { buildStripeAppearance } from "../system/stripeAppearance.js"
import { checkoutSession } from "../system/checkoutSession.js"

export default function SubscribeCheckout({ pid, checkout }) {
    const [clientSecret, setClientSecret] = useState(null)
    const [stripePromise, setStripePromise] = useState(null)
    const [isTest, setIsTest] = useState(false)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
        api("subscribe/checkout/initiate", { pid })
            .then((res) => {
                setClientSecret(res.clientSecret)
                setStripePromise(getStripePromise(res.publicKey))
                setIsTest(!!res.isTest)
            })
            .catch(() => {
                setError("Failed to initiate subscription.")
            })
            .finally(() => {
                setLoading(false)
            })
    }, [pid])

    const product = checkout.product
    if (!product) return null

    const ready = !loading && clientSecret && stripePromise

    return (
        <div className="v1 min-h-full bg-background text-foreground">
            <header className="border-b border-border">
                <div className="max-w-5xl mx-auto px-4 py-4 flex items-center">
                    <AlgoBuilderLogo height={22} />
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 py-8">
                <h1 className="text-section text-foreground mb-6">Checkout</h1>

                {error ? (
                    <p className="text-caption text-danger">{error}</p>
                ) : !ready ? (
                    <div className="h-32 rounded-md border border-border bg-card shimmer-bar" />
                ) : (
                    <Elements
                        stripe={stripePromise}
                        options={{
                            clientSecret,
                            appearance: buildStripeAppearance(),
                            loader: "auto",
                        }}
                    >
                        <SubscribeCheckoutForm product={product} isTest={isTest} />
                    </Elements>
                )}
            </main>
        </div>
    )
}

function SubscribeCheckoutForm({ product, isTest }) {
    const stripe = useStripe()
    const elements = useElements()

    const [processing, setProcessing] = useState(false)
    const [paymentProcessed, setPaymentProcessed] = useState(false)
    const [error, setError] = useState(null)
    const [termsAccepted, setTermsAccepted] = useState(false)

    const currencySymbol = getSymbolCurrency(product.currency)
    const order = buildSubscribeOrder({ product, currencySymbol })

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!stripe || !elements) return

        setProcessing(true)
        setError(null)

        const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: window.location.origin + "/checkout/complete",
            },
            redirect: "if_required",
        })

        if (confirmError) {
            if (confirmError.type === "card_error" || confirmError.type === "validation_error") {
                setError(confirmError.message)
            } else {
                setError("The payment could not be processed. Please try again.")
            }
            setProcessing(false)
            return
        }

        if (paymentIntent?.status !== "succeeded") {
            setError("The payment was not successful. Please try again.")
            setProcessing(false)
            return
        }

        try {
            await api("subscribe/checkout/confirm", { paymentId: paymentIntent.id })
            checkoutSession.removePid()
            setPaymentProcessed(true)
            setTimeout(() => {
                window.location.href = "/"
            }, 3000)
        } catch {
            setError("Failed to complete subscription.")
            setProcessing(false)
        }
    }

    if (paymentProcessed) {
        return (
            <div className="max-w-xl rounded-md border border-border bg-card p-6 flex flex-col items-start gap-3">
                <CheckCircle2 className="text-success" size={32} strokeWidth={1.75} />
                <div>
                    <h2 className="text-section text-foreground font-semibold">Payment successful</h2>
                    <p className="text-body text-muted-foreground mt-1">
                        Your subscription is active. Redirecting you to your dashboard...
                    </p>
                </div>
            </div>
        )
    }

    return (
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
                <h2 className="text-section text-foreground mb-4">Order Summary</h2>
                <div className="bg-card border border-border rounded-md p-5">
                    <h3 className="text-section text-foreground font-semibold mb-3">
                        {order.summaryTitle}
                    </h3>
                    <div className="flex flex-col gap-2 text-body">
                        {order.summaryRows.map((rowItem, i) => (
                            <div key={i} className="flex justify-between">
                                <span className="text-muted-foreground">{rowItem.label}</span>
                                <span className="text-foreground">{rowItem.value}</span>
                            </div>
                        ))}
                    </div>
                    <Separator className="my-4" />
                    <div className="flex justify-between text-body font-semibold">
                        <span className="text-foreground">{order.dueTodayLabel}</span>
                        <span className="text-primary text-section">{order.dueTodayPrice}</span>
                    </div>
                    {/* <div className="flex justify-between text-caption text-muted-foreground mt-1">
                        <span>Next billing:</span>
                        <span>{order.nextBilling}</span>
                    </div> */}
                    {order.footer && (
                        <p className="text-caption text-muted-foreground mt-3">{order.footer}</p>
                    )}
                    {isTest && (
                        <p className="text-caption text-muted-foreground mt-3 italic">
                            This is a test payment. No real charges will be made.
                        </p>
                    )}
                </div>
            </div>

            <div>
                <h2 className="text-section text-foreground mb-4">Payment</h2>
                <div className="bg-card border border-border rounded-md p-5">
                    <p className="text-caption text-muted-foreground mb-4">
                        Secure checkout. Your card is never stored.
                    </p>

                    <div className="mb-4">
                        <PaymentElement options={{ layout: "tabs" }} />
                    </div>

                    <label className="flex items-start gap-2 text-caption text-muted-foreground mb-4">
                        <input
                            type="checkbox"
                            required
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            className="mt-0.5"
                        />
                        <span>
                            I have read and agree to the{" "}
                            <a
                                className="text-primary underline"
                                target="_blank"
                                rel="noreferrer"
                                href="https://algobuilder.com/terms-and-conditions/"
                            >
                                Terms and Conditions
                            </a>{" "}
                            and{" "}
                            <a
                                className="text-primary underline"
                                target="_blank"
                                rel="noreferrer"
                                href="https://algobuilder.com/privacy-policy/"
                            >
                                Privacy Policy
                            </a>
                            .
                        </span>
                    </label>

                    {error && <p className="text-caption text-danger mb-3">{error}</p>}

                    <Button
                        type="submit"
                        className="w-full text-label"
                        disabled={!stripe || processing || !termsAccepted}
                    >
                        {processing ? "Processing..." : order.ctaLabel}
                    </Button>
                </div>
                <p className="text-caption text-muted-foreground mt-3 font-medium flex items-center gap-1.5">
                    <ShieldCheck size={13} strokeWidth={1.5} className="shrink-0" />
                    Your payment information is secure and encrypted.
                </p>
            </div>
        </form>
    )
}

function buildSubscribeOrder({ product, currencySymbol }) {
    const isAnnual = product.billingCycle === "yearly"

    const summaryRows = [
        { label: "Plan:", value: product.name },
        {
            label: "Billing:",
            value: isAnnual ? "Annual, auto-renew" : "Monthly, auto-renew",
        },
        { label: "Cancel:", value: "Anytime" },
    ]

    const dueTodayPrice = `${currencySymbol}${product.price}`
    const nextBilling = isAnnual
        ? `${currencySymbol}${product.price} / year`
        : `${currencySymbol}${product.price} / month`

    const ctaLabel = isAnnual
        ? `Subscribe to ${product.name} - ${currencySymbol}${product.price}/yr`
        : `Subscribe to ${product.name} - ${currencySymbol}${product.price}/mo`

    const footer = isAnnual
        ? `You'll be charged ${currencySymbol}${product.price} today, then yearly. Cancel any time.`
        : `You'll be charged ${currencySymbol}${product.price} today, then ${currencySymbol}${product.price} per month. Cancel any time.`

    return {
        summaryTitle: `${product.name} plan`,
        summaryRows,
        dueTodayLabel: "Due today:",
        dueTodayPrice,
        nextBilling,
        ctaLabel,
        footer,
    }
}
